import type { MouseEvent } from 'react';

/*
 * The hover strobe of the header and footer buttons: two quick inverts, then
 * back to rest. Run through the browser's own Web Animations, which is what
 * framer-motion did with it, so these buttons need no animation library.
 *
 * The rest state is CSS (.nav-strobe and .nav-strobe-active in globals.css).
 */

const STROBE_ID = 'nav-strobe';
const STROBE_FRAMES = [0, 1, 0, 1, 0].map(amount => ({ filter: `invert(${amount})` }));

function strobesOn(el: HTMLElement) {
  return el.getAnimations().filter(animation => animation.id === STROBE_ID);
}

/** Plays the strobe, then holds its last frame for as long as the pointer stays. */
export function startStrobe(el: HTMLElement) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  strobesOn(el).forEach(animation => animation.cancel());
  el.animate(STROBE_FRAMES, { id: STROBE_ID, duration: 600, easing: 'ease-in-out', fill: 'forwards' });
}

/** Eases the button back to rest from wherever the strobe got to. */
export function settleStrobe(el: HTMLElement) {
  const strobes = strobesOn(el);
  if (strobes.length === 0) return;
  const from = getComputedStyle(el).filter;
  strobes.forEach(animation => animation.cancel());
  el.animate([{ filter: from, offset: 0 }], { duration: 300, easing: 'cubic-bezier(0.25, 0.1, 0.35, 1)' });
}

/** Spread onto the element that strobes under the pointer. The button of an open menu stays still. */
export const strobeOnHover = {
  onMouseEnter: (event: MouseEvent<HTMLElement>) => {
    if (event.currentTarget.getAttribute('aria-expanded') !== 'true') startStrobe(event.currentTarget);
  },
  onMouseLeave: (event: MouseEvent<HTMLElement>) => settleStrobe(event.currentTarget),
};
